import { Fragment, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

import styled from 'styled-components'
import Swal from 'sweetalert2'
import axios from 'axios'
import config from '../../../config'
import moment from 'moment'
import Cookies from 'js-cookie'
import { ROUND_CLOSE_TYPE, schema } from './validations'
import ImageUploadForm from './ImageUploadForm'
import { DEVICE_TYPE } from '../../../constants/configs'

const RoundCloseDetailForm = ({ value, onSave }) => {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    mode: 'onChange',
    resolver: zodResolver(schema),
  })

  const onSubmit = async (data) => {
    await Swal.fire({
      title: 'บันทึกข้อมูล',
      text: 'คุณต้องการบันทึกข้อมูลหรือไม่',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'บันทึก',
      cancelButtonText: 'ยกเลิก',
      showLoaderOnConfirm: true,
      preConfirm: async () => {
        try {
          const res = await axios.patch(
            `${config.apiAdminGateway}/api/v1/admin/configs/roundCloseDetail`,
            { ...data, description: data.description.trim() || ' ' },
            {
              headers: { Authorization: `Bearer ${Cookies.get('accessToken')}` },
            },
          )

          if (res.data.status.code !== 0) {
            throw new Error('Error updating round close detail')
          }

          Swal.fire({
            title: 'บันทึกข้อมูลสำเร็จ',
            html: 'หากต้องการปิดรอบ<br/>ให้เปลี่ยนสถานะปิดรอบซื้อขาย',
            icon: 'success',
          }).then(() => onSave('roundCloseDetail', data))
          return true
        } catch (error) {
          console.error(error)
          Swal.showValidationMessage('เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง')
          return false
        }
      },
    })
  }

  useEffect(() => {
    if (value.type) {
      reset({
        roundCloseType: value.type,
        title: value.title,
        description: value.descriptions.trim(),
        isSchedule: value.isSchedule,
        openTime: moment(value.openTime, 'DD-MM-YY HH:mm').format('YYYY-MM-DDTHH:mm'),
      })
    }
  }, [reset, value])

  return (
    <div>
      <Form as="form" onSubmit={handleSubmit(onSubmit)}>
        <InputContainer>
          <label htmlFor="selectOption">
            ประเภทการปิดรอบ<span style={{ color: 'red' }}>*</span>
          </label>
          <StyledSelect
            id="selectOption"
            {...register('roundCloseType', {
              onChange: (event) => {
                setValue('roundCloseType', event.target.value)
                setValue('imageUrl', value.images[event.target.value])
              },
            })}
          >
            <option disabled value="">
              ระบุ
            </option>
            <option value={ROUND_CLOSE_TYPE.ROUND_CLOSE}>ปิดรอบ</option>
            <option value={ROUND_CLOSE_TYPE.TEMPORARY_ROUND_CLOSE}>ปิดรอบชั่วคราว</option>
          </StyledSelect>
          {errors.type && <ErrorMessageText>{errors.type.message}</ErrorMessageText>}
        </InputContainer>
        {watch('roundCloseType') === ROUND_CLOSE_TYPE.TEMPORARY_ROUND_CLOSE && (
          <Fragment>
            <InputContainer>
              <label htmlFor="title">
                หัวข้อ<span style={{ color: 'red' }}>*</span>
              </label>
              <StyledInput type="text" id="title" {...register('title')} />
              {errors.title && <ErrorMessageText>{errors.title.message}</ErrorMessageText>}
            </InputContainer>
            <InputContainer>
              <label htmlFor="description">คำอธิบาย</label>
              <StyledInput type="text" id="description" {...register('description')} />
              {errors.descriptions && <ErrorMessageText>{errors.descriptions.message}</ErrorMessageText>}
            </InputContainer>
          </Fragment>
        )}
        <InputContainer>
          <label htmlFor="dateTimeSelect">
            เวลาเปิดรอบถัดไป<span style={{ color: 'red' }}>*</span>
          </label>
          <StyledInput type="datetime-local" id="dateTimeSelect" {...register('openTime')} />
          {errors.openTime && <ErrorMessageText>{errors.openTime.message}</ErrorMessageText>}
        </InputContainer>
        <InputContainer style={{ flexDirection: 'row', gap: '1rem' }}>
          <label htmlFor="isSchedule">เปิดอัตโนมัติเมื่อถึงเวลา​</label>
          <Checkbox type="checkbox" id="isSchedule" {...register('isSchedule')} />
        </InputContainer>
        <SubmitButton>บันทึก</SubmitButton>
      </Form>
      <ImageUploadForm name={watch('roundCloseType')} deviceType={DEVICE_TYPE.WEBSITE} onSave={onSave} />
      <ImageUploadForm name={watch('roundCloseType')} deviceType={DEVICE_TYPE.MOBILE} onSave={onSave} />
    </div>
  )
}

export default RoundCloseDetailForm

const Form = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const StyledInput = styled.input`
  padding: 0.5rem;
  border: 1px solid #e0e0e0;
  border-radius: 0.5rem;
  font-size: 1rem;
  font-weight: 500;
  transition: 0.3s;
  &:focus {
    border-color: #007bff;
  }
`

const StyledSelect = styled.select`
  padding: 0.5rem;
  border: 1px solid #e0e0e0;
  border-radius: 0.5rem;
  font-size: 1rem;
  font-weight: 500;
  transition: 0.3s;
  :focus {
    border-color: #007bff;
  }
`

const Checkbox = styled.input`
  min-width: 1.5rem;
  margin-bottom: 8px;
  aspect-ratio: 1;
  &::after {
    display: none !important;
  }
`

const CloseButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: red;
  color: white;
  border: none;
  padding: 0.5rem;
  border-radius: 50%;
  aspect-ratio: 1;
`

const ImageUploadLabel = styled.label`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0.5rem;
  background-color: #f2f2f2;
  border: 1px solid #e0e0e0;
  border-radius: 0.5rem;
  cursor: pointer;
  color: #333;
  font-size: 1rem;
  font-weight: 500;
  transition: 0.3s;
  &:hover {
    background-color: #e0e0e0;
  }
`

const ErrorMessageText = styled.span`
  color: red;
  font-size: 0.8rem;
`

const SubmitButton = styled.button`
  padding: 0.5rem 1rem;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 500;
  transition: 0.3s;
  &:hover {
    background-color: #0056b3;
  }
`
